import React, { useState } from 'react';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import { User, Mail, Edit2, Save, X, DollarSign, Shield } from 'lucide-react';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
];

export default function Profile() {
  const { user, token, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    bio: user?.bio || '',
    preferred_currency: user?.preferred_currency || 'USD',
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/auth/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      setEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', color: 'bg-chakra-crown', desc: 'Full system access' },
      user_admin: { label: 'User Admin', color: 'bg-chakra-third', desc: 'Manage users' },
      content_admin: { label: 'Content Admin', color: 'bg-chakra-throat', desc: 'Manage content' },
      practitioner_admin: { label: 'Practitioner Admin', color: 'bg-chakra-heart', desc: 'Manage practitioners' },
      practitioner: { label: 'Practitioner', color: 'bg-cosmic-turquoise', desc: 'Offer healing services' },
      user: { label: 'Member', color: 'bg-cosmic-purple', desc: 'Community member' },
    };
    return badges[role] || badges.user;
  };

  const badge = getRoleBadge(user?.role);

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="profile-page">
      <div className="glass-card p-8 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-cinzel gold-text">Your Profile</h1>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="cosmic-btn flex items-center gap-2 text-sm py-2 px-4"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-lg text-cosmic-starWhite/60 hover:text-cosmic-starWhite border border-cosmic-gold/20 hover:border-cosmic-gold/40 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="cosmic-btn flex items-center gap-2 text-sm py-2 px-4"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-cosmic-goldLight/30 border-t-cosmic-goldLight rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cosmic-gold/30 to-cosmic-purple/30 flex items-center justify-center border-2 border-cosmic-gold/40">
              <User className="w-12 h-12 text-cosmic-goldLight" />
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-cinzel text-cosmic-starWhite">{user?.name}</h2>
              <p className="text-cosmic-starWhite/60 font-cormorant">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start">
                <Shield className="w-4 h-4 text-cosmic-gold" />
                <span className={`text-xs px-2 py-1 rounded ${badge.color} text-white`}>
                  {badge.label}
                </span>
                <span className="text-cosmic-starWhite/40 text-xs">{badge.desc}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm text-cosmic-starWhite/60 mb-2 font-cormorant">Name</label>
              {editing ? (
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/60" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="cosmic-input w-full pl-12"
                    data-testid="name-input"
                  />
                </div>
              ) : (
                <p className="text-cosmic-starWhite font-cormorant text-lg">{user?.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-cosmic-starWhite/60 mb-2 font-cormorant">Email</label>
              {editing ? (
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/60" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="cosmic-input w-full pl-12"
                    data-testid="email-input"
                  />
                </div>
              ) : (
                <p className="text-cosmic-starWhite font-cormorant text-lg">{user?.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-cosmic-starWhite/60 mb-2 font-cormorant">Bio</label>
              {editing ? (
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="cosmic-input w-full h-24 resize-none"
                  placeholder="Tell us about your spiritual journey..."
                  data-testid="bio-input"
                />
              ) : (
                <p className="text-cosmic-starWhite/80 font-cormorant">
                  {user?.bio || 'No bio added yet'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-cosmic-starWhite/60 mb-2 font-cormorant flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Preferred Currency
              </label>
              {editing ? (
                <select
                  value={formData.preferred_currency}
                  onChange={(e) => setFormData({ ...formData, preferred_currency: e.target.value })}
                  className="cosmic-input w-full"
                  data-testid="currency-select"
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code} className="bg-cosmic-deepSpace">
                      {c.symbol} {c.code} - {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-cosmic-starWhite font-cormorant text-lg">
                  {CURRENCIES.find(c => c.code === user?.preferred_currency)?.name || 'US Dollar'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-cinzel text-lg text-cosmic-goldLight mb-4">Account Details</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-cosmic-starWhite/60">Member Since</span>
            <span className="text-cosmic-starWhite">
              {new Date(user?.created_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-cosmic-starWhite/60">Account Status</span>
            <span className="text-chakra-heart">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}
